import { useQuery } from '@tanstack/react-query'
import { Link, Outlet, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { meQueryOptions } from '@/entities/employee'
import { unreadCountQueryOptions } from '@/entities/notification'
import { useSession } from '@/entities/session'
import { latestRunning, type Running, runningQueryOptions } from '@/entities/time-entry'
import { useLogout } from '@/features/logout'
import { useReleaseAll, useReleaseTask } from '@/features/take-in-work'
import { cn } from '@/shared/lib'
import { Avatar } from '@/shared/ui/avatar'
import { BrandLogo, EcosystemsLogo } from '@/shared/ui/brand'
import { Icon } from '@/shared/ui/icon'
import { Menu, MenuItem } from '@/shared/ui/menu'
import { RunningTimer } from '@/shared/ui/running-timer'
import { ToastViewport } from '@/shared/ui/toast'
import { ZoneBadge } from '@/shared/ui/zone-badge'
import { CustomerShell } from './customer-shell'
import { FocusSlot } from './focus-slot'
import { ProductSwitcher } from './product-switcher'
import { NavBurger, NavDrawer, type NavItem, NavItemLink, ShellSidebar } from './shell-nav'

const NAV = [
  { to: '/', icon: 'my-tasks', label: 'Мои', exact: true },
  { to: '/companies', icon: 'building', label: 'Компании' },
  { to: '/inbox', icon: 'bell', label: 'Инбокс' },
  { to: '/reports', icon: 'report', label: 'Отчёты' },
  { to: '/employees', icon: 'users', label: 'Сотрудники' },
  { to: '/focus', icon: 'focus', label: 'Фокус' },
] as const satisfies readonly NavItem[]

// Стоп из бара = «отпустить» (та же семантика, что CTA на карточке): интервал
// закрывается, задача уезжает в колонку, из которой её взяли.
function TimerBarStop({ running, compact = false }: { running: Running; compact?: boolean }) {
  const release = useReleaseTask({ id: running.taskId, boardId: running.boardId })
  return (
    <button
      type="button"
      aria-label={`Отпустить: ${running.taskTitle}`}
      title="Отпустить — вернётся туда, откуда взяли"
      onClick={() => release.mutate()}
      disabled={release.isPending}
      // -mr-2 p-2: тач-зона 40px вокруг 16px-иконки, визуально ряд не разъезжается.
      className={cn(
        'shrink-0 p-2 text-text-tertiary transition-colors hover:text-text-primary disabled:opacity-40',
        !compact && '-mr-2',
      )}
    >
      <Icon name="stop" size={16} />
    </button>
  )
}

// Список всех идущих задач: раскрывается из бейджа «+N». На мобайле — лист снизу
// (бар и так прижат к нижней кромке), на десктопе — панель над строкой сайдбара.
function RunningList({ list, onClose }: { list: Running[]; onClose: () => void }) {
  const releaseAll = useReleaseAll(list.map((r) => ({ id: r.taskId, boardId: r.boardId })))

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <>
      <button
        type="button"
        aria-label="Закрыть список"
        onClick={onClose}
        className="fixed inset-0 z-40 cursor-default bg-bg-base/60 md:bg-transparent"
      />
      <div
        className={cn(
          'z-50 flex flex-col gap-1 border border-border-default bg-bg-surface shadow-[0_18px_44px_rgba(0,0,0,0.5)]',
          // Мобайл: лист от нижней кромки, над ним ничего не перекрывает палец.
          'fixed inset-x-0 bottom-0 rounded-t-card px-2 pt-2 pb-safe',
          // Десктоп: панель раскрывается вверх над строкой бара.
          'md:absolute md:inset-x-2 md:top-auto md:bottom-full md:mb-2 md:rounded-card md:p-2',
        )}
      >
        <p className="label px-2 py-1 text-text-tertiary">В работе: {list.length}</p>
        {list.map((r) => (
          <div key={r.taskId} className="flex min-w-0 items-center gap-2 rounded-control px-2 py-1">
            <RunningTimer startedAt={r.startedAt} size="sm" />
            <Link
              to="/tasks/$taskId"
              params={{ taskId: r.taskId }}
              onClick={onClose}
              className="min-w-0 flex-1 truncate text-sm text-text-secondary transition-colors hover:text-text-primary"
            >
              {r.taskTitle}
            </Link>
            <TimerBarStop running={r} compact />
          </div>
        ))}
        <button
          type="button"
          onClick={() => {
            releaseAll()
            onClose()
          }}
          className="mt-1 min-h-11 rounded-control px-2 text-left text-sm text-text-tertiary transition-colors hover:bg-bg-elevated hover:text-text-primary md:min-h-0 md:py-2"
        >
          Остановить все
        </button>
      </div>
    </>
  )
}

// Постоянный индикатор трекинга — ядро ценности №2. Пусто → приглушённая строка;
// идёт → живой тик от startedAt + задача (клик → карточка) + стоп в один клик.
// Несколько задач разом легальны: строка держит самую свежую, остальные — за
// бейджем «+N», поэтому поведение самой строки не зависит от их числа.
function TimerBar() {
  const running = useQuery(runningQueryOptions)
  const [listOpen, setListOpen] = useState(false)
  const list = running.data ?? []
  const latest = latestRunning(list)

  if (!latest) {
    return (
      <div className="flex items-center gap-2 text-sm text-text-tertiary">
        <span className="h-2 w-2 rounded-pill bg-border-strong" aria-hidden />В работе ничего нет
      </div>
    )
  }

  const rest = list.length - 1
  return (
    <div className="relative flex min-w-0 items-center gap-2 text-sm">
      <RunningTimer startedAt={latest.startedAt} size="sm" />
      <Link
        to="/tasks/$taskId"
        params={{ taskId: latest.taskId }}
        className="min-w-0 flex-1 truncate text-text-secondary transition-colors hover:text-text-primary"
      >
        {latest.taskTitle}
      </Link>
      {rest > 0 && (
        <button
          type="button"
          aria-expanded={listOpen}
          aria-label={`Ещё ${rest} в работе — показать список`}
          onClick={() => setListOpen((v) => !v)}
          data-numeric
          className="shrink-0 rounded-pill bg-bg-elevated px-2 py-0.5 font-mono text-xs text-text-secondary transition-colors hover:text-text-primary"
        >
          +{rest}
        </button>
      )}
      <TimerBarStop running={latest} />
      {listOpen && <RunningList list={list} onClose={() => setListOpen(false)} />}
    </div>
  )
}

// Раздел «Инбокс» + счётчик непрочитанных (поллинг 30с внутри queryOptions).
// Бейдж — маленький accent-числовой, не заливка (accent — дефицит). 0 → без бейджа.
function InboxNavItem({
  item,
  onNavigate,
}: {
  item: NavItem
  onNavigate: (() => void) | undefined
}) {
  const unread = useQuery(unreadCountQueryOptions)
  const count = unread.data ?? 0
  return (
    <NavItemLink
      item={item}
      onNavigate={onNavigate}
      ariaLabel={count > 0 ? `${item.label}, ${count} непрочитанных` : item.label}
      badge={
        count > 0 && (
          <span
            data-numeric
            aria-hidden
            className="flex min-w-[1rem] items-center justify-center rounded-pill bg-accent px-1 font-mono text-[10px] leading-4 text-text-inverse"
          >
            {count > 9 ? '9+' : count}
          </span>
        )
      }
    />
  )
}

// Инбокс — единственный раздел команды со своей спецификой; остальные рисует
// общий каркас.
const renderEmployeeItem = (item: NavItem, onNavigate: (() => void) | undefined) =>
  item.to === '/inbox' ? <InboxNavItem item={item} onNavigate={onNavigate} /> : null

// Профиль сотрудника в правом верхнем углу шапки: аватар + имя + chevron, меню
// (Настройки · Выйти) раскрывается вниз (side="bottom", align="end").
function AccountMenu() {
  const me = useQuery(meQueryOptions)
  const logout = useLogout()
  const navigate = useNavigate()
  const name = me.data?.name ?? '—'
  return (
    <Menu
      align="end"
      ariaLabel={me.data ? `Аккаунт: ${name}` : 'Аккаунт'}
      trigger={
        <span className="flex items-center gap-2">
          <Avatar name={name} size="sm" />
          <span className="hidden max-w-40 truncate text-sm sm:block">{name}</span>
          <Icon name="chevron-down" size={16} className="text-text-tertiary" />
        </span>
      }
    >
      {(close) => (
        <>
          <MenuItem
            onSelect={() => {
              close()
              void navigate({ to: '/settings' })
            }}
          >
            <span className="flex items-center gap-2">
              <Icon name="settings" size={16} />
              Настройки
            </span>
          </MenuItem>
          <MenuItem
            onSelect={() => {
              close()
              logout.mutate()
            }}
          >
            <span className="flex items-center gap-2">
              <Icon name="logout" size={16} />
              Выйти
            </span>
          </MenuItem>
        </>
      )}
    </Menu>
  )
}

export function AppShell({ onOpenSearch }: { onOpenSearch?: () => void }) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { session } = useSession()

  // Заказчик живёт в другом приложении: витрина вместо трекера (00-architecture
  // § 6). Разводим каркасом целиком, а не набором `isEmployee &&` по всей шапке —
  // иначе внутренние разделы утекают в разметку и держатся только на условиях.
  if (session?.kind === 'customer') return <CustomerShell session={session} />

  return (
    <>
      <div className="flex h-dvh flex-col overflow-hidden bg-bg-base text-text-primary">
        {/* Шапка на всю ширину: бренд · свитчер · поиск по центру окна · профиль */}
        <header className="relative flex h-14 shrink-0 items-center border-b border-border-default">
          {/* Бренд-зона = ширина сайдбара, её правая граница продолжает вертикаль
              сайдбара: слева от линии — экосистема, справа — текущий продукт. */}
          <div className="flex h-full shrink-0 items-center gap-3 px-4 sm:px-6 md:w-60 md:border-r md:border-border-default">
            <NavBurger onOpen={() => setDrawerOpen(true)} />
            <Link
              to="/"
              aria-label="STIT Task — на главную"
              className="flex min-w-0 items-center rounded-control opacity-90 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-border-accent focus-visible:outline-none"
            >
              {/* На узком экране — только знак: полный логотип с дескриптором
                  съедает место у свитчера и правой группы. */}
              <BrandLogo className="h-4 sm:hidden" />
              <EcosystemsLogo className="hidden h-5 sm:block" />
            </Link>
          </div>

          {/* Свитчер — первый элемент контентной колонки: отвечает «в каком сервисе я». */}
          <div className="flex h-full min-w-0 items-center px-2 sm:px-4">
            <ProductSwitcher current="task" wordmark="TASK" />
          </div>

          {/* Поиск — визуальное поле ввода; клик открывает ⌘K-палитру (реюз).
              Центрируется по ширине окна, а не по контентной колонке: длина имени
              продукта слева не должна двигать поле. Ширина — вьюпорт минус боковые
              зоны, чтобы поле не наезжало на свитчер и профиль; ниже lg места на
              поле не остаётся — там поиск живёт иконкой в правой группе. */}
          <button
            type="button"
            onClick={onOpenSearch}
            className="absolute left-1/2 hidden h-9 w-[calc(100vw-48rem)] max-w-sm -translate-x-1/2 items-center gap-2 rounded-control border border-border-default bg-bg-sunken px-3 text-sm text-text-tertiary transition-colors hover:border-border-strong hover:text-text-secondary focus:border-border-accent focus:outline-none lg:flex"
          >
            <Icon name="search" size={16} className="shrink-0" />
            <span className="flex-1 text-left">Поиск</span>
            <kbd className="hidden rounded-sm border border-border-default bg-bg-raised px-1 font-mono text-[11px] leading-4 text-text-tertiary lg:inline">
              ⌘K
            </kbd>
          </button>

          <div className="ml-auto flex h-full shrink-0 items-center gap-1 px-4 sm:px-6">
            {/* Зона организации подписана явно: всё время в продукте — в ней, а
                часы устройства у каждого свои. */}
            <ZoneBadge />
            <FocusSlot />
            <button
              type="button"
              aria-label="Поиск"
              onClick={onOpenSearch}
              className="inline-flex size-9 items-center justify-center rounded-control text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary lg:hidden"
            >
              <Icon name="search" size={20} />
            </button>
            <AccountMenu />
          </div>
        </header>

        <div className="flex min-h-0 flex-1">
          {/* Сайдбар — десктоп; начинается сразу с навигации (лого живёт в шапке). */}
          <ShellSidebar items={NAV} renderItem={renderEmployeeItem} footer={<TimerBar />} />

          <main className="min-w-0 flex-1 overflow-y-auto pb-[calc(3.5rem+env(safe-area-inset-bottom))] [scrollbar-gutter:stable] md:pb-0">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Таймер-бар — мобайл: фиксированный bottom-bar (критичнее навигации, всегда
          на виду). Контент над ним разгружен паддингом на <main>. Высота min-, а не
          фиксированная: снизу добавляется safe-area, иначе бар лёг бы под
          home-indicator (index.html объявляет viewport-fit=cover). */}
      <div className="fixed inset-x-0 bottom-0 z-40 flex min-h-14 items-center border-t border-border-default bg-bg-base px-4 pb-safe px-safe md:hidden">
        <div className="min-w-0 flex-1">
          <TimerBar />
        </div>
      </div>

      <NavDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        home="/"
        items={NAV}
        renderItem={renderEmployeeItem}
      />

      <ToastViewport />
    </>
  )
}
